import React from 'react'
import { Box } from 'components/Layout'
import {
  usePriceAddForm,
  PriceFieldName,
  getErrorMessage,
  useProducts,
  GetDefaultPrice_getDefaultPrice_defaultPrices,
  useTestLocations,
  getPriceGqlCompliant,
  useAddDefaultPrice,
  useEditDefaultPrice,
  SubLocation,
} from '@modmd/data'
import Button from 'components/Button'
import TextInput from 'components/TextInput'
import { SelectInput } from 'components/SelectInput'
import { useToaster } from 'utils/useToaster'
import { Sublocation } from 'sharedComponents/Sublocation/Sublocation'
import { getProducts, getLocations } from '../../FinancesList/utils'

export interface CreateDefaultPriceProps {
  defaultPriceItem: GetDefaultPrice_getDefaultPrice_defaultPrices | undefined
  onSubmit: () => Promise<void>
}

const CreateEditDefaultPrices = ({ defaultPriceItem, onSubmit }: CreateDefaultPriceProps) => {
  const { setToastMessage } = useToaster()

  const { products, isProductsDataLoading } = useProducts()

  const productOptions = React.useMemo(() => (products ? getProducts(products) : []), [products])

  const { data: testLocations, loading: isTestLocationsLoading } = useTestLocations({
    showDeleted: false,
  })

  const testLocationsOptions = React.useMemo(
    () => (testLocations?.testLocations ? getLocations(testLocations) : []),
    [testLocations]
  )

  const [editDefaultPrice, { loading: isEditLoading }] = useEditDefaultPrice({
    onCompleted: () => {
      setToastMessage('Data successfully updated', 'success')
    },
  })

  const [addDefaultPrice, { loading: isAddLoading }] = useAddDefaultPrice({
    onCompleted: () => {
      setToastMessage('Data successfully added', 'success')
    },
  })

  const priceForm = usePriceAddForm({
    validationSchema: 'fullDefaultPrice',
    initialValues: {
      id: defaultPriceItem?.id,
      productId: defaultPriceItem?.productId,
      locationId: defaultPriceItem?.locationId,
      price: defaultPriceItem?.price,
      minQuantity: defaultPriceItem?.minQuantity,
      sublocation: defaultPriceItem?.sublocation || SubLocation.Lab,
    },
    onSubmit: async (values, formikHelpers) => {
      if (values.id) {
        await editDefaultPrice({
          variables: {
            inputData: getPriceGqlCompliant(values, [
              PriceFieldName.ID,
              PriceFieldName.PRODUCT_ID,
              PriceFieldName.LOCATION_ID,
              PriceFieldName.PRICE,
              PriceFieldName.MIN_QUANTITY,
              PriceFieldName.SUBLOCATION,
            ]),
          },
        })
      } else
        await addDefaultPrice({
          variables: {
            inputData: getPriceGqlCompliant(values, [
              PriceFieldName.PRODUCT_ID,
              PriceFieldName.LOCATION_ID,
              PriceFieldName.PRICE,
              PriceFieldName.MIN_QUANTITY,
              PriceFieldName.SUBLOCATION,
            ]),
          },
        })
      formikHelpers.resetForm()
      await onSubmit()
    },
  })

  return (
    <>
      <Box display="grid" marginTop="1rem">
        <TextInput
          name={PriceFieldName.PRICE}
          type="number"
          value={priceForm?.values?.price}
          onChange={priceForm?.handleChange}
          onBlur={priceForm?.handleBlur}
          errorMessage={getErrorMessage(priceForm?.touched.price, priceForm?.errors.price)}
          label="Price"
          placeholder="0.00"
          isFullWidth
        />
        <TextInput
          name={PriceFieldName.MIN_QUANTITY}
          type="number"
          value={priceForm?.values.minQuantity}
          onChange={priceForm?.handleChange}
          onBlur={priceForm?.handleBlur}
          errorMessage={getErrorMessage(
            priceForm?.touched.minQuantity,
            priceForm?.errors.minQuantity
          )}
          label="Min. Quantity"
          placeholder="0"
          isFullWidth
        />
        {!isProductsDataLoading && (
          <SelectInput
            name="productId"
            label="Product"
            value={priceForm?.values.productId || ''}
            onChange={priceForm?.handleChange}
            options={[{ label: 'Select an item', value: '', disabled: true }, ...productOptions]}
          />
        )}
        {!isTestLocationsLoading && (
          <SelectInput
            name="locationId"
            label="Location"
            value={priceForm?.values.locationId || ''}
            onChange={priceForm?.handleChange}
            options={[
              { label: 'Select a location', value: '', disabled: true },
              ...testLocationsOptions,
            ]}
          />
        )}
        <Sublocation
          sublocation={priceForm?.values?.sublocation || ''}
          refetch={() => {}}
          onChange={priceForm?.handleChange}
        />
      </Box>
      <Box display="flex" justifyContent="flex-end" alignItems="center">
        <Button
          disabled={!priceForm.isValid}
          isFetching={isAddLoading || isEditLoading}
          onClick={() => priceForm.handleSubmit()}
        >
          Save
        </Button>
      </Box>
    </>
  )
}

export { CreateEditDefaultPrices }
